// components/CapsuleDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Copy, 
  Settings, 
  ExternalLink,
  Search,
  Filter,
  X,
  Check
} from 'lucide-react';
import { dbService } from '../lib/supabase';

const CapsuleDetail = () => {
  const { id: capsuleId } = useParams();
  const navigate = useNavigate();
  
  // Debug logging
  console.log('CapsuleDetail rendered with capsuleId:', capsuleId);
  
  const [capsule, setCapsule] = useState(null);
  const [activeTab, setActiveTab] = useState('items');
  const [items, setItems] = useState([]);
  const [outfits, setOutfits] = useState([]);
  const [cheatSheets, setCheatSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [categories, setCategories] = useState([]);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showCapsuleForm, setShowCapsuleForm] = useState(false);
  const [showCheatSheetForm, setShowCheatSheetForm] = useState(false);
  const [clients, setClients] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCheatSheet, setEditingCheatSheet] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    link: '',
    price: '',
    brand: '',
    category: '',
    color: '',
    size: '',
    capsule_id: capsuleId
  });
  const [capsuleFormData, setCapsuleFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    password: '',
    is_published: false
  });
  const [cheatSheetFormData, setCheatSheetFormData] = useState({
    name: '',
    description: '',
    selectedItems: []
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [cheatSheetFile, setCheatSheetFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cheatSheetSearchTerm, setCheatSheetSearchTerm] = useState('');

  // Predefined category options
  const categoryOptions = [
    'Tops', 'Bottoms', 'Dresses', 'Outerwear', 'Shoes', 'Accessories',
    'Bags', 'Jewelry', 'Undergarments', 'Activewear', 'Swimwear',
    'Sleepwear', 'Formal', 'Casual', 'Work', 'Other'
  ];

  useEffect(() => {
    loadCapsule();
    loadClients();
  }, [capsuleId]);

  useEffect(() => {
    if (activeTab === 'items') {
      loadItems();
    } else if (activeTab === 'outfits') {
      loadOutfits();
    } else if (activeTab === 'cheatsheets') {
      loadCheatSheets();
    }
  }, [activeTab, capsuleId]);

  const loadCapsule = async () => {
    setLoading(true);
    try {
      const { data, error } = await dbService.getCapsule(capsuleId);
      if (error) {
        console.error('Error loading capsule:', error);
      } else if (data) {
        setCapsule(data);
        // Set capsule form data when capsule loads
        setCapsuleFormData({
          name: data.name,
          description: data.description || '',
          client_id: data.client_id,
          password: data.password || '',
          is_published: data.is_published
        });
      } else {
        console.error('No capsule data returned');
      }
    } catch (err) {
      console.error('Exception loading capsule:', err);
    }
    setLoading(false);
  };

  const loadClients = async () => {
    const { data, error } = await dbService.getClients();
    if (!error) {
      setClients(data || []);
    }
  };

  const loadItems = async () => {
    const { data, error } = await dbService.getItemsForCapsule(capsuleId);
    if (!error) {
      setItems(data || []);
      // Extract unique categories
      const existingCategories = [...new Set(data?.map(item => item.category).filter(Boolean))];
      const allCategories = [...new Set([...categoryOptions, ...existingCategories])].sort();
      setCategories(allCategories);
    }
  };

  const loadOutfits = async () => {
    const { data, error } = await dbService.getOutfits(capsuleId);
    if (!error) {
      setOutfits(data || []);
    }
  };

  const loadCheatSheets = async () => {
    const { data, error } = await dbService.getCheatSheets(capsuleId);
    if (!error) {
      setCheatSheets(data || []);
    }
  };

  const handleCreateOutfit = async () => {
    const outfitName = prompt('Enter outfit name:');
    if (outfitName) {
      const { data, error } = await dbService.createOutfit({
        name: outfitName,
        capsule_id: capsuleId,
        order_index: 0
      });
      
      if (!error) {
        navigate(`/dashboard/capsules/${capsuleId}/outfits/${data.id}`);
      }
    }
  };

  const handleDeleteOutfit = async (outfitId) => {
    if (window.confirm('Delete this outfit?')) {
      await dbService.deleteOutfit(outfitId);
      loadOutfits();
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/capsule/${capsuleId}`;
    navigator.clipboard.writeText(url);
    alert('Link copied to clipboard!');
  };

  const handleEditCapsule = () => {
    console.log('Edit capsule clicked');
    console.log('Current showCapsuleForm:', showCapsuleForm);
    setShowCapsuleForm(true);
    console.log('Setting showCapsuleForm to true');
  };

  const handleSubmitCapsule = async (e) => {
    e.preventDefault();
    const result = await dbService.updateCapsule(capsuleId, capsuleFormData);
    
    if (!result.error) {
      loadCapsule();
      setShowCapsuleForm(false);
    }
  };

  const resetCapsuleForm = () => {
    setCapsuleFormData({
      name: capsule?.name || '',
      description: capsule?.description || '',
      client_id: capsule?.client_id || '',
      password: capsule?.password || '',
      is_published: capsule?.is_published || false
    });
    setShowCapsuleForm(false);
  };

  // Cheat Sheet management functions
  const handleSubmitCheatSheet = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imageUrl = '';
      if (cheatSheetFile) {
        const tempId = editingCheatSheet ? editingCheatSheet.id : Date.now().toString();
        const { data: uploadData, error: uploadError } = await dbService.uploadCheatSheetImage(cheatSheetFile, tempId);
        if (!uploadError) {
          imageUrl = uploadData;
        }
      }

      const sheetData = {
        name: cheatSheetFormData.name,
        description: cheatSheetFormData.description,
        capsule_id: capsuleId,
        image_url: imageUrl || (editingCheatSheet ? editingCheatSheet.image_url : ''),
      };

      let result;
      if (editingCheatSheet) {
        result = await dbService.updateCheatSheet(editingCheatSheet.id, sheetData);
      } else {
        result = await dbService.createCheatSheet(sheetData);
      }

      if (!result.error) {
        await dbService.updateCheatSheetItems(
          editingCheatSheet ? editingCheatSheet.id : result.data.id, 
          cheatSheetFormData.selectedItems
        );
        
        loadCheatSheets();
        resetCheatSheetForm();
      }
    } catch (err) {
      console.error('Error saving cheat sheet:', err);
    }
    setUploading(false);
  };

  const handleEditCheatSheet = async (sheet) => {
    setEditingCheatSheet(sheet);
    setCheatSheetFormData({
      name: sheet.name,
      description: sheet.description || '',
      selectedItems: []
    });
    
    const { data: sheetItems } = await dbService.getCheatSheetItems(sheet.id);
    setCheatSheetFormData(prev => ({
      ...prev,
      selectedItems: sheetItems?.map(si => si.item_id) || []
    }));
    
    setShowCheatSheetForm(true);
  };

  const handleDeleteCheatSheet = async (sheetId) => {
    if (window.confirm('Are you sure you want to delete this cheat sheet?')) {
      await dbService.deleteCheatSheet(sheetId);
      loadCheatSheets();
    }
  };

  const resetCheatSheetForm = () => {
    setCheatSheetFormData({
      name: '',
      description: '',
      selectedItems: []
    });
    setEditingCheatSheet(null);
    setCheatSheetFile(null);
    setShowCheatSheetForm(false);
    setCheatSheetSearchTerm('');
  };

  const toggleCheatSheetItemSelection = (itemId) => {
    setCheatSheetFormData(prev => ({
      ...prev,
      selectedItems: prev.selectedItems.includes(itemId)
        ? prev.selectedItems.filter(id => id !== itemId)
        : [...prev.selectedItems, itemId]
    }));
  };

  const handleCheatSheetFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCheatSheetFile(file);
    }
  };

  // Item management functions
  const handleSubmitItem = async (e) => {
    e.preventDefault();
    setUploading(true);

    let photoUrl = '';
    if (selectedFile) {
      const tempId = editingItem ? editingItem.id : Date.now().toString();
      const { data: uploadData, error: uploadError } = await dbService.uploadItemPhoto(selectedFile, tempId);
      if (!uploadError) {
        photoUrl = uploadData;
      }
    }

    const itemData = {
      ...formData,
      capsule_id: capsuleId,
      photo_url: photoUrl || (editingItem ? editingItem.photo_url : ''),
    };

    let result;
    if (editingItem) {
      result = await dbService.updateItem(editingItem.id, itemData);
    } else {
      result = await dbService.createItem(itemData);
    }

    if (!result.error) {
      loadItems();
      resetItemForm();
    }
    setUploading(false);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      link: item.link || '',
      price: item.price || '',
      brand: item.brand || '',
      category: item.category || '',
      color: item.color || '',
      size: item.size || '',
      capsule_id: item.capsule_id
    });
    setSelectedFile(null);
    setShowItemForm(true);
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      await dbService.deleteItem(id);
      loadItems();
    }
  };

  const resetItemForm = () => {
    setFormData({
      name: '',
      description: '',
      link: '',
      price: '',
      brand: '',
      category: '',
      color: '',
      size: '',
      capsule_id: capsuleId
    });
    setEditingItem(null);
    setSelectedFile(null);
    setShowItemForm(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterBy === 'all' || item.category === filterBy;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading capsule...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!capsule) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-8">
          <p className="text-gray-600">Capsule not found</p>
          <button
            onClick={() => navigate('/dashboard/capsules')}
            className="mt-4 bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800"
          >
            Back to Capsules
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/dashboard/capsules')}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-light text-gray-900">{capsule.name}</h1>
            <p className="text-gray-600">{capsule.clients?.name}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => window.open(`/capsule/${capsuleId}`, '_blank')}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Preview"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={copyLink}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Copy Link"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              console.log('Settings button clicked');
              handleEditCapsule();
            }}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Edit Capsule"
          >
            <Settings className="w-4 h-4" />
          </button>
          
          {/* Test button */}
          <button
            onClick={() => {
              console.log('Test button clicked');
              alert('Test button works!');
            }}
            className="p-2 text-red-400 hover:text-red-600"
            title="Test"
          >
            TEST
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('items')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'items'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Items ({items.length})
          </button>
          <button
            onClick={() => setActiveTab('outfits')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'outfits'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Outfits ({outfits.length})
          </button>
          <button
            onClick={() => setActiveTab('cheatsheets')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'cheatsheets'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Cheat Sheets ({cheatSheets.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'items' && (
        <div>
          {/* Items Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-medium">Items</h2>
            <button
              onClick={() => setShowItemForm(true)}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-800"
            >
              <Plus className="w-4 h-4" />
              <span>Add Item</span>
            </button>
          </div>

          {/* Search and Filter */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="text-gray-400 w-4 h-4" />
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Items Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {item.photo_url && (
                  <img
                    src={item.photo_url}
                    alt={item.name}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900 truncate">{item.name}</h3>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={() => handleEditItem(item)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-1 text-sm text-gray-600">
                    {item.brand && <p className="font-medium">{item.brand}</p>}
                    {item.category && <p className="text-xs bg-gray-100 px-2 py-1 rounded-full inline-block">{item.category}</p>}
                    {item.color && <p>Color: {item.color}</p>}
                    {item.size && <p>Size: {item.size}</p>}
                    {item.price && <p className="font-medium text-gray-900">{item.price}</p>}
                  </div>
                  
                  {item.description && (
                    <p className="text-sm text-gray-700 mt-2 line-clamp-2">{item.description}</p>
                  )}
                  
                  {item.link && (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
                    >
                      <span>Shop Now</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm || filterBy !== 'all' ? 'No items match your search.' : 'No items in this capsule yet.'}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'outfits' && (
        <div>
          {/* Outfits Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-medium">Outfits</h2>
            <button
              onClick={handleCreateOutfit}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-800"
            >
              <Plus className="w-4 h-4" />
              <span>New Outfit</span>
            </button>
          </div>

          {/* Outfits Grid */}
          <div className="grid gap-4">
            {outfits.map((outfit) => (
              <div
                key={outfit.id}
                className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/dashboard/capsules/${capsuleId}/outfits/${outfit.id}`)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{outfit.name}</h3>
                    {outfit.description && (
                      <p className="text-gray-600 mt-1">{outfit.description}</p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">
                      {outfit.outfit_items?.length || 0} items selected
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteOutfit(outfit.id);
                    }}
                    className="p-2 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {outfits.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No outfits created yet.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'cheatsheets' && (
        <div>
          {/* Cheat Sheets Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-medium">Cheat Sheets</h2>
            <button
              onClick={() => setShowCheatSheetForm(true)}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-800"
            >
              <Plus className="w-4 h-4" />
              <span>New Cheat Sheet</span>
            </button>
          </div>

          {/* Cheat Sheets Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cheatSheets.map((sheet) => (
              <div
                key={sheet.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {sheet.image_url && (
                  <img
                    src={sheet.image_url}
                    alt={sheet.name}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900 truncate">{sheet.name}</h3>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={() => handleEditCheatSheet(sheet)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCheatSheet(sheet.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {sheet.description && (
                    <p className="text-sm text-gray-600 mb-2">{sheet.description}</p>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    {sheet.cheat_sheet_items?.length || 0} items selected
                  </p>
                </div>
              </div>
            ))}
          </div>

          {cheatSheets.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No cheat sheets created yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Item Form Modal */}
      {showItemForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-medium">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h3>
              <button
                onClick={resetItemForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitItem} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Item Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                  required
                />
                <input
                  type="text"
                  placeholder="Brand"
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                >
                  <option value="">Select Category</option>
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                />
                <input
                  type="text"
                  placeholder="Size"
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Price"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                />
                <input
                  type="url"
                  placeholder="Shopping Link"
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                />
              </div>
              
              <textarea
                placeholder="Description (optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                rows="3"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item Photo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-1">
                    Selected: {selectedFile.name}
                  </p>
                )}
                {editingItem && editingItem.photo_url && !selectedFile && (
                  <p className="text-sm text-gray-600 mt-1">
                    Current photo will be kept
                  </p>
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {uploading ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={resetItemForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Capsule Edit Form Modal */}
      {showCapsuleForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-medium">Edit Capsule</h3>
              <button
                onClick={resetCapsuleForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitCapsule} className="space-y-4">
              <select
                value={capsuleFormData.client_id}
                onChange={(e) => setCapsuleFormData({ ...capsuleFormData, client_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                required
              >
                <option value="">Select Client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Capsule Name"
                value={capsuleFormData.name}
                onChange={(e) => setCapsuleFormData({ ...capsuleFormData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                required
              />
              <textarea
                placeholder="Description (optional)"
                value={capsuleFormData.description}
                onChange={(e) => setCapsuleFormData({ ...capsuleFormData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                rows="3"
              />
              <input
                type="text"
                placeholder="Password for client access"
                value={capsuleFormData.password}
                onChange={(e) => setCapsuleFormData({ ...capsuleFormData, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
              />
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={capsuleFormData.is_published}
                  onChange={(e) => setCapsuleFormData({ ...capsuleFormData, is_published: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-gray-700">Published (visible to client)</span>
              </label>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={resetCapsuleForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cheat Sheet Form Modal */}
      {showCheatSheetForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-medium">
                {editingCheatSheet ? 'Edit Cheat Sheet' : 'New Cheat Sheet'}
              </h3>
              <button
                onClick={resetCheatSheetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitCheatSheet} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Cheat Sheet Name"
                  value={cheatSheetFormData.name}
                  onChange={(e) => setCheatSheetFormData({ ...cheatSheetFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Collage Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCheatSheetFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                  />
                  {cheatSheetFile && (
                    <p className="text-sm text-gray-600 mt-1">
                      Selected: {cheatSheetFile.name}
                    </p>
                  )}
                </div>
              </div>
              
              <textarea
                placeholder="Description (optional)"
                value={cheatSheetFormData.description}
                onChange={(e) => setCheatSheetFormData({ ...cheatSheetFormData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                rows="3"
              />

              {/* Item Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Items ({cheatSheetFormData.selectedItems.length} selected)
                </label>
                
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={cheatSheetSearchTerm}
                      onChange={(e) => setCheatSheetSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                </div>

                {/* Items Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {items
                    .filter(item => 
                      item.name.toLowerCase().includes(cheatSheetSearchTerm.toLowerCase()) ||
                      item.brand?.toLowerCase().includes(cheatSheetSearchTerm.toLowerCase())
                    )
                    .map((item) => (
                      <div
                        key={item.id}
                        onClick={() => toggleCheatSheetItemSelection(item.id)}
                        className={`relative cursor-pointer rounded-lg border-2 p-2 transition-all ${
                          cheatSheetFormData.selectedItems.includes(item.id)
                            ? 'border-gray-900 bg-gray-50'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        {item.photo_url && (
                          <img
                            src={item.photo_url}
                            alt={item.name}
                            className="w-full h-20 object-cover rounded mb-2"
                          />
                        )}
                        <p className="text-xs font-medium text-gray-900 truncate">{item.name}</p>
                        <p className="text-xs text-gray-500 truncate">{item.brand}</p>
                        
                        {cheatSheetFormData.selectedItems.includes(item.id) && (
                          <div className="absolute top-1 right-1 bg-gray-900 text-white rounded-full p-1">
                            <Check className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {uploading ? 'Saving...' : editingCheatSheet ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={resetCheatSheetForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Debug: Show current state */}
      {console.log('showCapsuleForm state:', showCapsuleForm)}
      {console.log('capsuleFormData:', capsuleFormData)}
    </div>
  );
};

export default CapsuleDetail;